import { FunderRecord, FunderFilter, FunderStats, DealSummary, AccountGroup, AssociationChange, AssociationLabel, AccountFunders, FunderInfo, AllCompaniesData, CompanyInfo } from '../types'

export class FundersService {
  private static instance: FundersService
  private rawData: FunderRecord[] = []
  private deals: DealSummary[] = []
  private isLoaded = false
  private changes: AssociationChange[] = []
  private readonly CHANGES_STORAGE_KEY = 'funders-association-changes'

  static getInstance(): FundersService {
    if (!FundersService.instance) {
      FundersService.instance = new FundersService()
    }
    return FundersService.instance
  }

  async loadData(): Promise<void> {
    if (this.isLoaded) return

    try {
      await this.parseCSVData()
      this.loadChanges()
      this.applyChanges()
      this.groupByDeals()
      this.isLoaded = true
    } catch (error) {
      console.error('Error loading funders data:', error)
      throw error
    }
  }

  private loadChanges(): void {
    try {
      const stored = localStorage.getItem(this.CHANGES_STORAGE_KEY)
      if (stored) {
        this.changes = JSON.parse(stored)
      }
    } catch (error) {
      console.error('Error loading changes:', error)
      this.changes = []
    }
  }

  private saveChanges(): void {
    try {
      localStorage.setItem(this.CHANGES_STORAGE_KEY, JSON.stringify(this.changes))
    } catch (error) {
      console.error('Error saving changes:', error)
    }
  }

  private applyChanges(): void {
    this.changes.forEach(change => {
      const record = this.rawData.find(r => 
        r.deal_id === change.deal_id && 
        r.company_id === change.company_id
      )
      if (record) {
        record.association_label = change.new_association
      }
    })
  }

  private async parseCSVData(): Promise<void> {
    try {
      // Fetch the CSV file from the public directory
      const response = await fetch('/2025-funders.csv')
      const csvText = await response.text()
      
      // Parse CSV manually
      const lines = csvText.trim().split('\n')
      // headers is parsed but not currently used
      // const headers = lines[0].split(',')
      
      this.rawData = lines.slice(1).map(line => {
        // Handle CSV parsing with potential commas in quoted fields
        const values = this.parseCSVLine(line)
        
        return {
          account: values[0] || '',
          deal_id: values[1] || '',
          deal_name: values[2] || '',
          deal_amount: parseFloat(values[3]) || 0,
          closed_won_date: values[4] || '',
          company_id: values[5] || '',
          company_name: values[6] || '',
          company_domain: values[7] || '',
          association_label: values[8] || '',
          association_category: values[9] || '',
          association_type_id: parseInt(values[10]) || 0,
          potential_funder: values[11] === 'True'
        }
      })
    } catch (error) {
      console.error('Error parsing CSV:', error)
      // Fallback to sample data for development
      await this.loadSampleData()
    }
  }

  private parseCSVLine(line: string): string[] {
    const result: string[] = []
    let current = ''
    let inQuotes = false
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    
    result.push(current.trim())
    return result
  }

  private async loadSampleData(): Promise<void> {
    // Fallback sample data
    this.rawData = [
      {
        account: 'Agilitas',
        deal_id: '34185118614',
        deal_name: 'Butlers Drycleaners Ltd - WN25HYO',
        deal_amount: 47203,
        closed_won_date: '2025-03-06T14:58:16.217Z',
        company_id: '30349102002',
        company_name: 'Rygor Commercials Ltd',
        company_domain: 'rygor.co.uk',
        association_label: 'Supplier',
        association_category: 'USER_DEFINED',
        association_type_id: 3,
        potential_funder: false
      },
      {
        account: 'Agilitas',
        deal_id: '34185118614',
        deal_name: 'Butlers Drycleaners Ltd - WN25HYO',
        deal_amount: 47203,
        closed_won_date: '2025-03-06T14:58:16.217Z',
        company_id: '30349355092',
        company_name: 'Metro Bank Asset Finance',
        company_domain: 'metrobankonline.co.uk',
        association_label: 'Funder',
        association_category: 'USER_DEFINED',
        association_type_id: 1,
        potential_funder: true
      },
      {
        account: 'Agilitas',
        deal_id: '34185118614',
        deal_name: 'Butlers Drycleaners Ltd - WN25HYO',
        deal_amount: 47203,
        closed_won_date: '2025-03-06T14:58:16.217Z',
        company_id: '32412013080',
        company_name: 'Butlers Drycleaners Ltd',
        company_domain: '',
        association_label: 'Primary',
        association_category: 'HUBSPOT_DEFINED',
        association_type_id: 5,
        potential_funder: false
      }
    ]
  }

  private groupByDeals(): void {
    const dealMap = new Map<string, DealSummary>()

    this.rawData.forEach(record => {
      if (!dealMap.has(record.deal_id)) {
        dealMap.set(record.deal_id, {
          deal_id: record.deal_id,
          deal_name: record.deal_name,
          account: record.account,
          deal_amount: record.deal_amount,
          closed_won_date: record.closed_won_date,
          companies: []
        })
      }

      const deal = dealMap.get(record.deal_id)!
      deal.companies.push({
        company_id: record.company_id,
        company_name: record.company_name,
        company_domain: record.company_domain,
        association_label: record.association_label,
        association_category: record.association_category,
        association_type_id: record.association_type_id,
        potential_funder: record.potential_funder
      })
    })

    this.deals = Array.from(dealMap.values())
  }

  async getDeals(filter?: FunderFilter): Promise<DealSummary[]> {
    await this.loadData()
    
    let filteredDeals = [...this.deals]

    if (filter) {
      if (filter.account) {
        filteredDeals = filteredDeals.filter(deal => 
          deal.account.toLowerCase().includes(filter.account!.toLowerCase())
        )
      }

      if (filter.association_label && filter.association_label.length > 0) {
        filteredDeals = filteredDeals.filter(deal =>
          deal.companies.some(company => 
            filter.association_label!.includes(company.association_label)
          )
        )
      }

      if (filter.potential_funder !== undefined && filter.potential_funder !== null) {
        filteredDeals = filteredDeals.filter(deal =>
          deal.companies.some(company => 
            company.potential_funder === filter.potential_funder
          )
        )
      }

      if (filter.search) {
        const searchLower = filter.search.toLowerCase()
        filteredDeals = filteredDeals.filter(deal =>
          deal.deal_name.toLowerCase().includes(searchLower) ||
          deal.account.toLowerCase().includes(searchLower) ||
          deal.companies.some(company =>
            company.company_name.toLowerCase().includes(searchLower)
          )
        )
      }

      if (filter.amount_range) {
        filteredDeals = filteredDeals.filter(deal =>
          deal.deal_amount >= filter.amount_range!.min &&
          deal.deal_amount <= filter.amount_range!.max
        )
      }

      if (filter.date_range) {
        const startDate = new Date(filter.date_range.start)
        const endDate = new Date(filter.date_range.end)
        filteredDeals = filteredDeals.filter(deal => {
          const dealDate = new Date(deal.closed_won_date)
          return dealDate >= startDate && dealDate <= endDate
        })
      }

      if (filter.exclude_no_companies) {
        filteredDeals = filteredDeals.filter(deal => deal.companies.length > 0)
      }
    }

    return filteredDeals
  }

  async getAccountGroups(filter?: FunderFilter): Promise<AccountGroup[]> {
    const deals = await this.getDeals(filter)
    const accountMap = new Map<string, DealSummary[]>()

    // Group deals by account
    deals.forEach(deal => {
      if (!accountMap.has(deal.account)) {
        accountMap.set(deal.account, [])
      }
      accountMap.get(deal.account)!.push(deal)
    })

    // Convert to AccountGroup array and sort deals within each account by date (newest first)
    const accountGroups = Array.from(accountMap.entries()).map(([account, deals]) => {
      const sortedDeals = deals.sort((a, b) => 
        new Date(b.closed_won_date).getTime() - new Date(a.closed_won_date).getTime()
      )
      
      return {
        account,
        deals: sortedDeals,
        total_amount: deals.reduce((sum, deal) => sum + deal.deal_amount, 0),
        deal_count: deals.length
      }
    })

    // Sort account groups by total amount (highest first)
    return accountGroups.sort((a, b) => b.total_amount - a.total_amount)
  }

  async getStats(filter?: FunderFilter): Promise<FunderStats> {
    const deals = await this.getDeals(filter)
    
    const totalAmount = deals.reduce((sum, deal) => sum + deal.deal_amount, 0)
    const allCompanies = deals.flatMap(deal => deal.companies)
    const potentialFunders = allCompanies.filter(company => company.potential_funder).length
    const accounts = Array.from(new Set(deals.map(deal => deal.account)))

    // Calculate top funders
    const funderMap = new Map<string, { deal_count: number; total_amount: number }>()
    
    deals.forEach(deal => {
      deal.companies
        .filter(company => company.potential_funder)
        .forEach(company => {
          const existing = funderMap.get(company.company_name) || { deal_count: 0, total_amount: 0 }
          existing.deal_count += 1
          existing.total_amount += deal.deal_amount
          funderMap.set(company.company_name, existing)
        })
    })

    const topFunders = Array.from(funderMap.entries())
      .map(([company_name, stats]) => ({ company_name, ...stats }))
      .sort((a, b) => b.total_amount - a.total_amount)
      .slice(0, 5)

    return {
      total_deals: deals.length,
      total_amount: totalAmount,
      potential_funders: potentialFunders,
      accounts,
      top_funders: topFunders
    }
  }

  async changeAssociation(
    dealId: string, 
    companyId: string, 
    newAssociation: AssociationLabel,
    user: string = 'User'
  ): Promise<void> {
    await this.loadData()
    
    // Find the record
    const record = this.rawData.find(r => 
      r.deal_id === dealId && 
      r.company_id === companyId
    )
    
    if (!record) {
      throw new Error('Record not found')
    }

    const oldAssociation = record.association_label

    // Don't create change if association is the same
    if (oldAssociation === newAssociation) {
      return
    }

    // Create change record
    const change: AssociationChange = {
      id: `${dealId}-${companyId}-${Date.now()}`,
      deal_id: dealId,
      deal_name: record.deal_name,
      company_id: companyId,
      company_name: record.company_name,
      company_domain: record.company_domain,
      old_association: oldAssociation,
      new_association: newAssociation,
      timestamp: new Date().toISOString(),
      user
    }

    // Remove any existing changes for this record (so we only track the latest change)
    this.changes = this.changes.filter(c => 
      !(c.deal_id === dealId && c.company_id === companyId)
    )

    // Add the new change
    this.changes.unshift(change)

    // Apply the change to the data
    record.association_label = newAssociation

    // Re-group deals with the updated data
    this.groupByDeals()

    // Save to localStorage
    this.saveChanges()
  }

  async undoChange(changeId: string): Promise<void> {
    await this.loadData()

    const changeIndex = this.changes.findIndex(c => c.id === changeId)
    if (changeIndex === -1) {
      throw new Error('Change not found')
    }

    const change = this.changes[changeIndex]
    
    // Find the record and revert it
    const record = this.rawData.find(r => 
      r.deal_id === change.deal_id && 
      r.company_id === change.company_id
    )
    
    if (record) {
      record.association_label = change.old_association
    }

    // Remove the change from the list
    this.changes.splice(changeIndex, 1)

    // Re-group deals with the reverted data
    this.groupByDeals()

    // Save to localStorage
    this.saveChanges()
  }

  async getChanges(): Promise<AssociationChange[]> {
    await this.loadData()
    return [...this.changes].sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )
  }

  async exportChanges(): Promise<string> {
    const changes = await this.getChanges()
    
    if (changes.length === 0) {
      return 'No changes to export'
    }

    // Create CSV format
    const headers = [
      'Timestamp', 'Deal Name', 'Company Name', 'Company Domain',
      'Old Association', 'New Association', 'User'
    ]
    
    const rows = changes.map(change => [
      new Date(change.timestamp).toLocaleString(),
      `"${change.deal_name}"`,
      `"${change.company_name}"`,
      `"${change.company_domain || ''}"`,
      change.old_association,
      change.new_association,
      change.user
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    return csvContent
  }

  async exportAllCompanies(filter?: FunderFilter): Promise<string> {
    const data = await this.getAllCompanyAssociations(filter)
    
    if (data.companies.length === 0) {
      return 'No companies to export'
    }

    // Create CSV format
    const headers = [
      'Account', 'Company Name', 'Company Domain', 'Total Amount', 'Deal Count',
      'Association Labels'
    ]
    
    const rows = data.companies.map(company => [
      `"${company.account}"`,
      `"${company.company_name}"`,
      `"${company.company_domain || ''}"`,
      company.total_amount,
      company.deal_count,
      `"${company.association_labels.join('; ')}"`
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    return csvContent
  }

  async exportFunders(filter?: FunderFilter): Promise<string> {
    const accountFunders = await this.getAccountFunders(filter)
    
    if (accountFunders.length === 0) {
      return 'No funders to export'
    }

    // Create CSV format
    const headers = [
      'Account', 'Company Name', 'Company Domain', 'Total Amount', 'Deal Count'
    ]
    
    const rows: string[][] = []
    accountFunders.forEach(account => {
      account.funders.forEach(funder => {
        rows.push([
          `"${account.account}"`,
          `"${funder.company_name}"`,
          `"${funder.company_domain || ''}"`,
          funder.total_amount.toString(),
          funder.deal_count.toString()
        ])
      })
    })

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    return csvContent
  }

  async getAccountFunders(filter?: FunderFilter): Promise<AccountFunders[]> {
    const deals = await this.getDeals(filter)
    const accountMap = new Map<string, Map<string, FunderInfo>>()

    // Group funders by account and company
    deals.forEach(deal => {
      if (!accountMap.has(deal.account)) {
        accountMap.set(deal.account, new Map<string, FunderInfo>())
      }

      const accountFunders = accountMap.get(deal.account)!
      
      // Find funders in this deal
      const dealFunders = deal.companies.filter(company => company.association_label.toLowerCase() === 'funder')
      
      if (dealFunders.length === 0) {
        // No funder defined for this deal
        const noFunderKey = 'no-funder-defined'
        if (!accountFunders.has(noFunderKey)) {
          accountFunders.set(noFunderKey, {
            company_id: noFunderKey,
            company_name: 'No funder defined',
            company_domain: '',
            deal_count: 0,
            total_amount: 0,
            deals: []
          })
        }

        const noFunderInfo = accountFunders.get(noFunderKey)!
        noFunderInfo.deal_count += 1
        noFunderInfo.total_amount += deal.deal_amount
        noFunderInfo.deals.push({
          deal_id: deal.deal_id,
          deal_name: deal.deal_name,
          deal_amount: deal.deal_amount,
          closed_won_date: deal.closed_won_date
        })
      } else {
        // Process each funder in the deal
        dealFunders.forEach(funder => {
          if (!accountFunders.has(funder.company_id)) {
            accountFunders.set(funder.company_id, {
              company_id: funder.company_id,
              company_name: funder.company_name,
              company_domain: funder.company_domain,
              deal_count: 0,
              total_amount: 0,
              deals: []
            })
          }

          const funderInfo = accountFunders.get(funder.company_id)!
          funderInfo.deal_count += 1
          funderInfo.total_amount += deal.deal_amount
          funderInfo.deals.push({
            deal_id: deal.deal_id,
            deal_name: deal.deal_name,
            deal_amount: deal.deal_amount,
            closed_won_date: deal.closed_won_date
          })
        })
      }
    })

    // Convert to AccountFunders array
    const accountFunders = Array.from(accountMap.entries()).map(([account, fundersMap]) => {
      const funders = Array.from(fundersMap.values())
        .sort((a, b) => {
          // Put "No funder defined" at the end
          if (a.company_id === 'no-funder-defined') return 1
          if (b.company_id === 'no-funder-defined') return -1
          // Sort others by total amount descending
          return b.total_amount - a.total_amount
        })

      return {
        account,
        funders,
        total_funders: funders.length,
        total_amount: funders.reduce((sum, funder) => sum + funder.total_amount, 0),
        total_deals: funders.reduce((sum, funder) => sum + funder.deal_count, 0)
      }
    })

    // Sort accounts by total amount descending
    return accountFunders.sort((a, b) => b.total_amount - a.total_amount)
  }

  async getAllCompanyAssociations(filter?: FunderFilter): Promise<AllCompaniesData> {
    const deals = await this.getDeals(filter)
    const companyMap = new Map<string, CompanyInfo>()
    const accountsWithNoAssociations = new Map<string, { deal_count: number; total_amount: number; deals: Array<any> }>()

    // Group companies by account + company combination
    deals.forEach(deal => {
      if (deal.companies.length === 0) {
        // Deal has no company associations
        if (!accountsWithNoAssociations.has(deal.account)) {
          accountsWithNoAssociations.set(deal.account, {
            deal_count: 0,
            total_amount: 0,
            deals: []
          })
        }

        const noAssocInfo = accountsWithNoAssociations.get(deal.account)!
        noAssocInfo.deal_count += 1
        noAssocInfo.total_amount += deal.deal_amount
        noAssocInfo.deals.push({
          deal_id: deal.deal_id,
          deal_name: deal.deal_name,
          deal_amount: deal.deal_amount,
          closed_won_date: deal.closed_won_date,
          association_label: 'None'
        })
      } else {
        deal.companies.forEach(company => {
          // Use account + company name as the key to keep companies separate per account
          const key = `${deal.account}|${company.company_name.toLowerCase().trim()}`
          
          if (!companyMap.has(key)) {
            companyMap.set(key, {
              company_id: company.company_id,
              company_name: company.company_name,
              company_domain: company.company_domain,
              account: deal.account,
              deal_count: 0,
              total_amount: 0,
              association_labels: [],
              deals: []
            })
          }

          const companyInfo = companyMap.get(key)!
          
          // Add unique association labels
          if (!companyInfo.association_labels.includes(company.association_label)) {
            companyInfo.association_labels.push(company.association_label)
          }
          
          // Update deal count and amount
          companyInfo.deal_count += 1
          companyInfo.total_amount += deal.deal_amount
          
          // Add deal details
          companyInfo.deals.push({
            deal_id: deal.deal_id,
            deal_name: deal.deal_name,
            deal_amount: deal.deal_amount,
            closed_won_date: deal.closed_won_date,
            association_label: company.association_label
          })
          
          // Update company domain if it's empty but we have a better one
          if (!companyInfo.company_domain && company.company_domain) {
            companyInfo.company_domain = company.company_domain
          }
        })
      }
    })

    // Add "No company associations" entries for each account that has deals without associations
    accountsWithNoAssociations.forEach((info, account) => {
      const noAssocKey = `${account}|no-company-associations`
      companyMap.set(noAssocKey, {
        company_id: 'no-company-associations',
        company_name: 'No company associations',
        company_domain: '',
        account: account,
        deal_count: info.deal_count,
        total_amount: info.total_amount,
        association_labels: ['None'],
        deals: info.deals
      })
    })

    // Convert to array and sort by account, then by total amount within account
    const companies = Array.from(companyMap.values())
      .sort((a, b) => {
        // First sort by account name
        if (a.account !== b.account) {
          return a.account.localeCompare(b.account)
        }
        // Put "No company associations" at the end within each account
        if (a.company_id === 'no-company-associations') return 1
        if (b.company_id === 'no-company-associations') return -1
        // Then sort by total amount descending within the same account
        return b.total_amount - a.total_amount
      })

    // Get unique accounts
    const uniqueAccounts = Array.from(new Set(companies.map(c => c.account)))

    return {
      companies,
      total_companies: companies.length,
      total_amount: companies.reduce((sum, company) => sum + company.total_amount, 0),
      total_deals: companies.reduce((sum, company) => sum + company.deal_count, 0),
      accounts: uniqueAccounts
    }
  }

  // Legacy method for backward compatibility
  async getFunderRecords(filter?: FunderFilter): Promise<FunderRecord[]> {
    await this.loadData()
    return this.rawData
  }
}

export const fundersService = FundersService.getInstance()